import { useState } from "react";
import { motion } from "framer-motion";

const streams = [
  { name: "Sourcing & Hiring", examples: "Writing job descriptions, screening candidates, matching talent to roles" },
  { name: "Onboarding", examples: "Verifying documents, running day-one induction, getting new hires set up" },
  { name: "Learning & Development", examples: "Recommending training, spotting skill gaps, delivering bite-sized learning" },
  { name: "HR Operations", examples: "Validating claims, spotting anomalies, processing documents" },
  { name: "Policy & Compliance", examples: "Answering policy questions, simplifying language, checking governance" },
  { name: "Engagement", examples: "Running campaigns, moderating events, generating internal content" },
  { name: "Performance & Goals", examples: "Aligning OKRs, recommending goals, surfacing performance signals" },
  { name: "Analytics & Reporting", examples: "Building dashboards, answering data questions in plain English, sending alerts" },
  { name: "Exits & Mobility", examples: "Processing full-and-final, matching internal moves, staying connected with alumni" },
];

export default function AIReadiness() {
  const [hoveredIdx, setHoveredIdx] = useState(null);

  return (
    <section
      id="ai-readiness"
      data-testid="ai-readiness-section"
      className="relative py-24 sm:py-32 lg:py-40"
    >
      <div className="max-w-7xl mx-auto px-6 sm:px-8 md:px-12">
        <motion.p
          className="overline mb-5"
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
        >
          Where AI fits
        </motion.p>

        <motion.h2
          data-testid="ai-readiness-heading"
          className="text-3xl sm:text-4xl lg:text-5xl font-semibold tracking-tight leading-[1.15] max-w-4xl mb-6"
          style={{ fontFamily: "var(--sv-heading-font)", color: "var(--sv-text-primary)" }}
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
        >
          Think of it like <span className="metallic-text">Gemini</span>,{" "}
          <br className="hidden sm:block" />
          but trained on your organisation.
        </motion.h2>

        <motion.p
          className="text-themed-body text-base sm:text-lg max-w-2xl mb-16 leading-relaxed"
          initial={{ opacity: 0, y: 15 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5, delay: 0.1 }}
        >
          AI works best when it knows your context — your policies, your processes, your people data.
          We look at nine areas of HR and identify exactly where AI can take real work off your team's plate,
          and where it can't.
        </motion.p>

        {/* 3×3 Grid */}
        <div
          className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-px"
          style={{ background: "var(--sv-border)" }}
        >
          {streams.map((stream, i) => (
            <motion.div
              key={stream.name}
              data-testid={`ai-stream-card-${i}`}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.4, delay: i * 0.05 }}
              onMouseEnter={() => setHoveredIdx(i)}
              onMouseLeave={() => setHoveredIdx(null)}
              className="p-6 sm:p-7"
              style={{
                background: hoveredIdx === i ? "var(--sv-bg-card-hover)" : "transparent",
                borderColor: hoveredIdx === i ? "var(--sv-border-hover)" : "var(--sv-border)",
                transition: "background 0.3s ease",
              }}
            >
              <h3
                className="text-base font-medium mb-3"
                style={{ fontFamily: "var(--sv-heading-font)", color: "var(--sv-text-primary)" }}
              >
                {stream.name}
              </h3>
              <p
                className="text-sm leading-relaxed"
                style={{
                  color: hoveredIdx === i ? "var(--sv-text-secondary)" : "var(--sv-text-muted)",
                  transition: "color 0.3s ease",
                }}
              >
                {stream.examples}
              </p>
            </motion.div>
          ))}
        </div>

        {/* Footnote */}
        <motion.p
          data-testid="ai-readiness-footnote"
          className="mt-12 text-sm italic text-center"
          style={{ fontFamily: "var(--sv-heading-font)", color: "var(--sv-text-muted)" }}
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          transition={{ delay: 0.3 }}
        >
          "We tell you where AI earns its place — and where it doesn't."
        </motion.p>
      </div>
    </section>
  );
}
